import { useEffect, useState, useMemo, useContext } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Divider,
  Button,
  Fade,
  ButtonGroup,
} from "@mui/material";
import Swal from "sweetalert2";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../../api/axios";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
} from "../../../Components/helpers/Helpers";
import LoadingPage from "../../../Components/Common/Loading/LoadingPage";
import { PermissionContext } from "../../../Components/Context/Context";

const Ver = () => {
  const navigate = useNavigate();
  const { goPanel, goTable, buildTableUrl } = useLocationHelpers();
  const { can } = useContext(PermissionContext);

  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const [loading, setLoading] = useState(false);
  const [contractData, setContractData] = useState(null);

  const getContract = async () => {
    try {
      setLoading(true);

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/contracts/show`,
        { id },
      );

      setContractData(data?.contract ?? null);
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo cargar el contrato", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getContract();
  }, [id]);

  /* ==========================
     FORMATEAR CONTENIDO
  =========================== */

  const formattedContract = useMemo(() => {
    const raw = contractData?.contract ?? "";

    if (!raw) return "";

    const hasHtmlTags = /<\/?[a-z][\s\S]*>/i.test(raw);

    if (hasHtmlTags) return raw;

    return raw
      .replace(/\r\n/g, "\n")
      .split("\n")
      .map((line) => `<p>${line}</p>`)
      .join("");
  }, [contractData?.contract]);

  return (
    <div className="fatherContainer">
      <CommonSubHeader
        title="Hojas de encargo - Contratos"
        subTitle="Ver"
        specialRute="contratos"
      />

      {loading && <LoadingPage isLoading />}

      {!loading && contractData && (
        <Fade in timeout={600}>
          <Box sx={{ p: 4 }}>
            <Paper
              elevation={4}
              sx={{
                maxWidth: 950,
                margin: "0 auto",
                padding: 4,
                borderRadius: 2,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
              }}>
              {/* Encabezado tipo firma */}
              <Box textAlign="center" mb={5}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: 1,
                    textTransform: "uppercase",
                  }}>
                  {contractData?.contract_title ?? "Contrato"}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{ mt: 1, color: "text.secondary" }}>
                  {contractData?.company_relation?.company_name ?? ""}
                </Typography>
              </Box>

              <Divider sx={{ mb: 5 }} />

              {/* Cuerpo contrato */}
              <Box
                sx={{
                  fontSize: "16px",
                  lineHeight: 1.9,
                  textAlign: "justify",
                  fontFamily: "Georgia, serif",
                  color: "#222",
                  "& p": {
                    marginBottom: 2,
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: formattedContract,
                }}
              />

              <Divider sx={{ mt: 6, mb: 4 }} />

              {/* Información económica */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} textAlign={"center"}>
                  <Typography variant="body2">
                    <strong>Precio:</strong>{" "}
                    {contractData?.contract_price ?? "No especificado"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} textAlign={"center"}>
                  <Typography variant="body2">
                    <strong>Porcentaje:</strong>{" "}
                    {contractData?.contract_percentage ?? "No especificado"}
                  </Typography>
                </Grid>
              </Grid>

              {/* Pie sobrio */}
              <Box mt={6} textAlign="center">
                <Typography variant="caption" color="text.secondary">
                  Documento generado digitalmente €¢ Despacho Jurí­dico
                </Typography>
              </Box>
            </Paper>

            {/* Botonera */}
            <Paper
              elevation={4}
              sx={{
                maxWidth: 950,
                margin: "0 auto",
                padding: 2,
                borderRadius: 2,
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                my: 2,
              }}>
              <ButtonGroup fullWidth>
                {can("contracts", "edit") && (
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      navigate(`/contratos/editar?id=${contractData?.id}`)
                    }>
                    Editar
                  </Button>
                )}

                <NavButton
                  fullWidth
                  variant="outlined"
                  color="error"
                  to={buildTableUrl("contratos")}
                  onClick={() => goTable("contratos")}>
                  Tabla
                </NavButton>

                <Button fullWidth onClick={goPanel}>
                  Inicio
                </Button>
              </ButtonGroup>
            </Paper>
          </Box>
        </Fade>
      )}
    </div>
  );
};

export default Ver;
